// src/admin/utils/adminHelpers.js
//
// Presentation-logic helpers for the Admin / Founder Dashboard.
// No API calls belong in this file.

/**
 * Tailwind class pair (bg + text) for a camp status badge.
 * Kept intentionally subtle — no large colored blocks.
 */
export function getCampStatusClass(status) {
  const key = (status || '').toUpperCase();

  const map = {
    SCHEDULED: 'bg-slate-100 text-slate-600',
    READY: 'bg-[#E8F8F7] text-[#0D8F8C]',
    IN_PROGRESS: 'bg-amber-50 text-amber-700',
    COMPLETED: 'bg-emerald-50 text-emerald-700',
    OVERDUE: 'bg-red-50 text-red-700',
  };

  return map[key] || 'bg-slate-100 text-slate-600';
}

/**
 * Returns whether a profit value is strictly positive.
 */
export function isPositiveProfit(profit) {
  const num = Number(profit);
  return Number.isFinite(num) && num > 0;
}

/**
 * Returns whether a profit value is strictly negative.
 */
export function isNegativeProfit(profit) {
  const num = Number(profit);
  return Number.isFinite(num) && num < 0;
}

/**
 * Text color class for a profit figure.
 */
export function getProfitClass(profit) {
  if (profit === null || profit === undefined || profit === '') {
    return 'text-slate-400';
  }
  if (isNegativeProfit(profit)) return 'text-red-600';
  if (isPositiveProfit(profit)) return 'text-emerald-600';
  return 'text-slate-600';
}

/**
 * Text color class for a margin percentage.
 * Thresholds are presentation-only and don't alter the backend value.
 */
export function getMarginClass(margin) {
  if (margin === null || margin === undefined || margin === '') {
    return 'text-slate-400';
  }

  const num = Number(margin);
  if (!Number.isFinite(num)) return 'text-slate-400';

  if (num < 0) return 'text-red-600';
  if (num < 15) return 'text-amber-600';
  return 'text-emerald-600';
}

/**
 * Safe numeric fallback for display counters (patients, camps, etc).
 */
export function num(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Human label for a status enum, e.g. "IN_PROGRESS" -> "In progress"
 */
export function formatStatusLabel(status) {
  if (!status) return 'Unknown';
  const clean = status.replace(/_/g, ' ').toLowerCase();
  return clean.charAt(0).toUpperCase() + clean.slice(1);
}
